/**
 * The program's control list on the Readiness view — V109-5 built-UI #2 / #3 (design round 3,
 * `program.js`): the approved filtered list that replaces the old open-controls grid under the roll-up.
 *
 *   "Controls · Core · 60"   [Not met 6] [Couldn't check 5] [Not yet tested 15] [Met 31] [Not applicable 3]
 *   (Sweep-checked only ×)                                      ← with `&source=sweep` (the "What changed" tile)
 *   one row per control: status · ref · title (New when this sweep moved it), what reads it, its action
 *
 * `?status=` picks the list (the roll-up legend links here); the heading takes focus and the count is
 * announced. Every count equals its list's rows: the program's counts come from the same rows
 * (`RollupData.rows`), the sweep-checked ones from the report's checked controls. Removing the chip shows
 * the program-wide list for the same status, with focus on the heading.
 *
 * Reads: none of its own (the roll-up's data). Writes: through each row's action (upload evidence:
 * editor+; accept the risk: owner / admin; server-enforced).
 */
import { useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { sourceNameOf, type ProgramCard, type ProgramManifest } from '@aigovops/core';
import { useOptionalAuth } from '../../lib/auth';
import { coveringAcceptance, objectiveItems, requirementItems, type CouldntItem } from '../../lib/couldnt-check';
import { BUCKET_LABEL, CONTROL_BUCKETS, changesHref, clockText, isBucket, unitNoun, unitOf, type ControlBucket } from '../../lib/sweep-model';
import { useAnnounce } from '../today/shared';
import { AcceptRisk, CouldntCheckChip, CouldntCheckFixes } from '../CouldntCheck';
import { UploadForObjective } from '../programs/CmmcAssess';
import { CONTROLS_ANCHOR, useReconcile, type ControlRow, type RollupData } from './ProgramRollup';
import './sweep.css';

interface ListRow { requirement_id: string; ref: string; title: string; bucket: ControlBucket; sweepMeta: string | null; row: ControlRow | null }

const CHIP_TONE: Record<ControlBucket, string | undefined> = { not_met: 'bad', couldnt_check: undefined, not_tested: 'dashed', met: 'ok', not_applicable: undefined };

export function ProgramControlList({ slug, prefix, m, card, data, canUpload, isAdmin }: {
  slug: string; prefix: string; m: ProgramManifest; card: ProgramCard; data: RollupData; canUpload: boolean; isAdmin: boolean;
}) {
  const [sp, setSp] = useSearchParams();
  const [live, announce] = useAnnounce();
  const qc = useQueryClient();
  const viewerId = useOptionalAuth()?.user?.id ?? '';
  const head = useRef<HTMLHeadingElement>(null);
  const status: ControlBucket = isBucket(sp.get('status')) ? sp.get('status') as ControlBucket : 'not_met';
  const sweepOnly = sp.get('source') === 'sweep';
  const unit = unitOf(m);
  const units = unitNoun(unit, 2);
  const r = data.report;
  const byId = useMemo(() => new Map(data.rows.map((x) => [x.requirement_id, x])), [data.rows]);
  // A control this sweep moved (its change row) — "New", and its Fix opens that change in place.
  const moved = useMemo(() => {
    const out = new Map<string, { changeId: string; resource: string | null; category: string }>();
    for (const c of r.changes) for (const a of c.affects) if (a.program === m.key && !out.has(a.requirement_id)) out.set(a.requirement_id, { changeId: c.id, resource: c.resource?.name ?? null, category: c.category });
    return out;
  }, [r.changes, m.key]);

  const sweepCounts: Record<ControlBucket, number> = { ...r.checked.now, not_tested: 0 };
  const counts: Record<ControlBucket, number> = sweepOnly ? sweepCounts : data.counts;
  const rows: ListRow[] = sweepOnly
    ? r.checked.requirements.filter((q) => q.state === status).map((q) => ({
      requirement_id: q.requirement_id, ref: q.ref, title: byId.get(q.requirement_id)?.title ?? q.title, bucket: q.state,
      sweepMeta: `${q.checks} sweep check${q.checks === 1 ? '' : 's'} · last reading ${clockText(q.last_reading_at)}`, row: byId.get(q.requirement_id) ?? null,
    }))
    : data.rows.filter((x) => x.bucket === status).map((x) => {
      const q = r.checked.requirements.find((y) => y.requirement_id === x.requirement_id);
      return { requirement_id: x.requirement_id, ref: x.ref, title: x.title, bucket: x.bucket, sweepMeta: q ? `${q.checks} sweep check${q.checks === 1 ? '' : 's'} · last reading ${clockText(q.last_reading_at)}` : null, row: x };
    });
  // What this sweep moved leads its list (the design's "New" rows first).
  const fresh = (x: ListRow) => { const mv = moved.get(x.requirement_id); return !!mv && ((x.bucket === 'not_met' && mv.category === 'newly_failing') || (x.bucket === 'couldnt_check' && mv.category === 'unevaluated') || (x.bucket === 'met' && mv.category === 'newly_passing')); };
  rows.sort((a, b) => Number(fresh(b)) - Number(fresh(a)));
  const errs = useReconcile(`list ${m.key}`, useMemo(() => ({ counts: data.counts, rows: data.rows, report: r }), [data.counts, data.rows, r]));
  const listMismatch = rows.length !== counts[status];

  const say = (b: ControlBucket, so: boolean, prefixText = '') => {
    const n = (so ? sweepCounts : data.counts)[b];
    announce(`${prefixText}${n} ${BUCKET_LABEL[b].toLowerCase()} ${unitNoun(unit, n)}${so ? ', sweep-checked only' : ''}`);
  };
  // Opened with ?status= (a legend count, a tile count): announce what the list holds.
  // BU-10: the guard is set when the announcement is made, and the cleanup cancels one still pending —
  // StrictMode's mount → cleanup → mount (dev / fixtures) used to set the guard, lose the message with
  // useAnnounce's timer, and never announce on the second mount.
  const announced = useRef<string | null>(null);
  const { hash } = useLocation();
  useEffect(() => {
    const key = `${sp.get('status') ?? ''}|${sweepOnly}`;
    if (!sp.get('status') || announced.current === key) return;
    const t = window.setTimeout(() => {
      announced.current = key; say(status, sweepOnly);
      // BU-17: a bare `?status=` (no #anchor for ProgramShell's hash focus) still lands on the list's heading.
      if (!hash) { head.current?.scrollIntoView?.({ block: 'start' }); head.current?.focus(); }
    }, 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const pick = (b: ControlBucket) => {
    const n = new URLSearchParams(sp); n.set('status', b);
    announced.current = `${b}|${sweepOnly}`;
    setSp(n, { replace: true });
    say(b, sweepOnly);
  };
  const clearSource = () => {
    const n = new URLSearchParams(sp); n.delete('source');
    announced.current = `${status}|false`;
    setSp(n, { replace: true });
    say(status, false, 'Filter removed · ');
    window.requestAnimationFrame(() => head.current?.focus());
  };

  return (
    <section className="sw-controls" aria-labelledby={CONTROLS_ANCHOR} data-part="control-list" data-reconcile={errs.length || listMismatch ? [...errs, listMismatch ? `${status}: ${rows.length} rows ≠ ${counts[status]}` : ''].filter(Boolean).join('; ') : 'ok'}>
      {live}
      <h3 className="gp-sec-h" id={CONTROLS_ANCHOR} ref={head} tabIndex={-1}>{`${units[0]!.toUpperCase()}${units.slice(1)} · ${data.label} · ${data.counts.total}`}</h3>
      <div className="sw-filterrow">
        <div className="sw-filters" role="group" aria-label={`Show ${units} by status`}>
          {CONTROL_BUCKETS.map((b) => (
            <button key={b} type="button" className="sw-filter" aria-pressed={b === status} data-bucket={b} onClick={() => pick(b)}>{BUCKET_LABEL[b]} <b>{counts[b]}</b></button>
          ))}
        </div>
        {sweepOnly ? <button type="button" className="sw-chip sw-chip-x" data-tone="info" onClick={clearSource} data-part="sweep-only-chip">Sweep-checked only<span className="sr-only">: remove filter</span><span aria-hidden="true">{' ×'}</span></button> : null}
      </div>
      {rows.length ? (
        <ul className="gp-owed-list sw-ctl-rows" aria-label={`${BUCKET_LABEL[status]} · ${rows.length} ${unitNoun(unit, rows.length)}`}>
          {rows.map((x) => <Row key={x.requirement_id} x={x} slug={slug} prefix={prefix} m={m} card={card} periodId={data.periodId} canUpload={canUpload} moved={moved.get(x.requirement_id) ?? null} />)}
        </ul>
      ) : sweepOnly && status === 'not_tested' ? (
        <div className="sw-empty"><p>{`No ${units} here: a sweep-checked ${unitNoun(unit, 1)} is never "not yet tested".`}</p>
          <button type="button" className="sw-textbtn" onClick={clearSource}>{`Show all not yet tested · ${data.counts.not_tested}`}</button></div>
      ) : <p className="sw-dim">{`No ${BUCKET_LABEL[status].toLowerCase()} ${units} right now.`}</p>}
      {status === 'couldnt_check' && isAdmin && rows.length ? (
        <AcceptRisk slug={slug} viewerId={viewerId} targets={rows.filter((x) => !coveringAcceptance(x.row?.govramp?.risk_acceptances ?? x.row?.fedramp?.risk_acceptances)).map((x) => {
          const kinds = [...new Set(itemsOf(x.row).map((i) => i.kind).filter((k): k is string => !!k))];
          return { label: `${x.ref} ${x.title}`, registerItemId: x.requirement_id, connection: kinds.length === 1 ? sourceNameOf(kinds[0]!) : null };
        })} onDone={() => void qc.invalidateQueries()} />
      ) : null}
    </section>
  );
}

function itemsOf(row: ControlRow | null): CouldntItem[] {
  if (!row) return [];
  if (row.govramp) return objectiveItems(row.govramp.couldnt_check ? { reasons: row.govramp.couldnt_check.reasons, unverified: 0, resources: 0, by_source: row.govramp.couldnt_check.by_source } : null);
  if (row.fedramp) return requirementItems(row.fedramp);
  return [];
}

function Row({ x, slug, prefix, m, card, periodId, canUpload, moved }: {
  x: ListRow; slug: string; prefix: string; m: ProgramManifest; card: ProgramCard; periodId: string | null; canUpload: boolean;
  moved: { changeId: string; resource: string | null; category: string } | null;
}) {
  const to = `${prefix}/programs/${m.key}/controls/${encodeURIComponent(x.requirement_id)}`;
  const isNew = !!moved && ((x.bucket === 'not_met' && moved.category === 'newly_failing') || (x.bucket === 'couldnt_check' && moved.category === 'unevaluated') || (x.bucket === 'met' && moved.category === 'newly_passing'));
  const label = `${x.ref} ${x.title}`;
  const g = x.row?.govramp;
  const f = x.row?.fedramp;
  let act: JSX.Element | null = null;
  if (x.bucket === 'not_met') {
    if (moved && moved.category === 'newly_failing') {
      act = <Link className="gp-linkbtn gp-linkbtn--compact" to={changesHref(prefix, card, { open: `fix-${moved.changeId}` })}>{`Fix ${moved.resource ?? x.ref}`}</Link>;
    } else {
      const failing = f?.crediting_controls.find((c) => c.state === 'fail') ?? f?.crediting_controls[0];
      const href = failing ? `${prefix}/findings?control=${encodeURIComponent(failing.control_id)}&requirement=${encodeURIComponent(x.ref)}` : `${prefix}/findings?requirement=${encodeURIComponent(x.ref)}`;
      act = <Link className="gp-linkbtn gp-linkbtn--compact" to={href}>Fix<span className="sr-only">{`: ${label}`}</span></Link>;
    }
  } else if (x.bucket === 'couldnt_check') {
    const items = itemsOf(x.row);
    act = items.length ? <CouldntCheckFixes items={items} prefix={prefix} ctx={{ program: m.key, requirement_id: x.requirement_id, ref: x.ref, ...(periodId ? { period_id: periodId } : {}) }} compact max={1} /> : null;
  } else if (x.bucket === 'not_tested') {
    act = canUpload && g?.open_objective_ids.length && periodId
      ? <UploadForObjective slug={slug} program={card.profile_id} periodId={periodId} refLabel={x.ref} label="Attach evidence" objectives={g.open_objective_ids.map((id) => ({ id, ref: id.split(':').pop()! }))} />
      : <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/evidence`}>Attach evidence<span className="sr-only">{`: ${label}`}</span></Link>;
  } else {
    act = <Link className="sw-textbtn" to={to}>Open<span className="sr-only">{` ${label}`}</span></Link>;
  }
  const accepted = coveringAcceptance(g?.risk_acceptances ?? f?.risk_acceptances);
  return (
    <li className="gp-ow" data-req={x.requirement_id} data-bucket={x.bucket} data-new={isNew ? 'true' : undefined}
      data-edge={isNew && x.bucket === 'not_met' ? 'bad' : isNew && x.bucket === 'couldnt_check' ? 'warn' : undefined}>
      <span className="gp-ow-main">
        <Link className="gp-ow-t" to={to}><span className="gr-ctl">{x.ref}</span>{' '}{x.title}</Link>
        <span className="gp-ow-meta">
          {x.bucket === 'couldnt_check' ? <CouldntCheckChip /> : <span className="sw-chip" data-tone={CHIP_TONE[x.bucket]}>{BUCKET_LABEL[x.bucket]}</span>}
          {isNew ? <span className="sw-new">New</span> : null}
          {accepted ? <span>Risk accepted</span> : null}
          <span>{x.sweepMeta ?? (x.bucket === 'not_tested' ? 'No sweep check · no evidence yet' : 'No sweep check')}</span>
        </span>
      </span>
      {act ? <span className="gp-ow-act">{act}</span> : null}
    </li>
  );
}
